/* eslint-disable react/jsx-no-literals -- pre-i18n migration; replace literals with t() and remove this directive (see docs/i18n-migration-plan.md) */
/**
 * HelpWidget — the floating "Need help planning?" button on public pages.
 * Two ways to reach the team: chat on WhatsApp (only when an admin has set
 * support_whatsapp_number) or request a callback, which lands in
 * Admin → Callback Requests.
 */
import React, { useState } from 'react';
import { Button, Modal, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import api from '@services/api';
import authService from '@services/authService';
import { useAuth } from '@hooks/useAuth';
import { whatsappLink } from '@utils/whatsappTemplates';

const NEEDS = [
  ['planning', 'Planning my wedding'],
  ['vendors', 'Finding vendors'],
  ['venue', 'Finding a venue'],
  ['budget', 'Budget advice'],
  ['other', 'Something else'],
];

const HelpWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [supportWhatsApp, setSupportWhatsApp] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', city: '', need: 'planning', weddingDate: '', message: '' });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState(null);

  const show = async () => {
    setOpen(true); setDone(null); setError(null);
    setForm(f => ({ ...f, name: f.name || [user?.firstName, user?.lastName].filter(Boolean).join(' '), phone: f.phone || user?.phone || '' }));
    try {
      const cfg = await authService.getPublicConfig();
      setSupportWhatsApp(cfg?.support?.whatsapp || null);
    } catch { setSupportWhatsApp(null); }
  };

  const set = (patch) => setForm(f => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { setError('Please add your name and phone number.'); return; }
    setSaving(true); setError(null);
    try {
      const r = await api.post('/help-requests', { ...form, weddingDate: form.weddingDate || null, channel: 'callback', page: location.pathname });
      setDone(r?.data?.message || "Thanks! Our team will get in touch with you soon.");
    } catch (e) {
      const errs = e?.response?.data?.errors;
      setError((errs && Object.values(errs)[0]?.[0]) || e?.response?.data?.message || 'Could not send your request. Please try again.');
    }
    setSaving(false);
  };

  return (
    <>
      <Button
        onClick={show}
        className="shadow rounded-pill px-3 d-flex align-items-center gap-2"
        style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1040, background: '#9B30FF', border: 'none' }}
        aria-label="Need help planning?"
      >
        <i className="bi bi-headset" /><span className="d-none d-sm-inline">Need help?</span>
      </Button>

      <Modal show={open} onHide={() => setOpen(false)} centered>
        <Modal.Header closeButton><Modal.Title className="h6">Need help planning your wedding?</Modal.Title></Modal.Header>
        <Modal.Body>
          {done ? (
            <Alert variant="success" className="mb-0"><i className="bi bi-check-circle me-2" />{done}</Alert>
          ) : (
            <>
              {supportWhatsApp && (
                <>
                  <Button
                    variant="success" className="w-100 mb-3"
                    onClick={() => window.open(whatsappLink(supportWhatsApp, 'Hi VowsEra, I need help planning my wedding.'), '_blank', 'noopener')}
                  >
                    <i className="bi bi-whatsapp me-2" />Chat with us on WhatsApp
                  </Button>
                  <div className="text-center text-muted small mb-3">or ask us to call you</div>
                </>
              )}
              {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
              <Row className="g-2">
                <Col xs={12}><Form.Control placeholder="Your name *" value={form.name} onChange={e => set({ name: e.target.value })} /></Col>
                <Col xs={7}><Form.Control type="tel" placeholder="Phone number *" value={form.phone} onChange={e => set({ phone: e.target.value })} /></Col>
                <Col xs={5}><Form.Control placeholder="City" value={form.city} onChange={e => set({ city: e.target.value })} /></Col>
                <Col xs={7}>
                  <Form.Select value={form.need} onChange={e => set({ need: e.target.value })} aria-label="What do you need help with?">
                    {NEEDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </Form.Select>
                </Col>
                <Col xs={5}><Form.Control type="date" title="Wedding date" value={form.weddingDate} onChange={e => set({ weddingDate: e.target.value })} /></Col>
                <Col xs={12}><Form.Control as="textarea" rows={2} placeholder="Anything we should know? (optional)" value={form.message} onChange={e => set({ message: e.target.value })} /></Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setOpen(false)}>{done ? 'Close' : 'Cancel'}</Button>
          {!done && <Button size="sm" style={{ background: '#9B30FF', border: 'none' }} onClick={submit} disabled={saving}>{saving ? <Spinner size="sm" /> : 'Request a callback'}</Button>}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default HelpWidget;
